import React from "react";
import {
  Gamepad2,
  Mic2,
  Tv,
  Zap,
  Beer,
  Sandwich,
  CupSoda,
  Users,
} from "lucide-react";

const JELLO =
  "https://static.prod-images.emergentagent.com/jobs/a4ebb2b7-97f3-40e7-b952-279fabbeaf1d/images/5e46a945d05c74238c402e079456de96e006ce28737f19e3f00f4aea8b0b2c58.png";
const POOL =
  "https://images.pexels.com/photos/5055749/pexels-photo-5055749.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SectionHeader = () => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
    <div>
      <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-[#FF2A2A] flex items-center gap-3">
        <span className="h-px w-8 bg-[#FF2A2A]" /> 02 — What You Get
      </div>
      <h2 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] text-[#F5F2E6]">
        CHEAP. STRONG.
        <br />
        <span className="text-[#FF2A2A]">DOWNRIGHT</span> ICONIC.
      </h2>
    </div>
    <p className="max-w-md text-[#A3A3A3] text-base md:text-lg">
      A neighborhood bar in the middle of downtown. Games in the back, karaoke
      on the mic, sports on the TVs, and pours that mean it.
    </p>
  </div>
);

const JelloHeroCard = () => (
  <div
    data-testid="highlight-jello"
    className="grain-card md:col-span-3 md:row-span-2 relative bg-[#1A1A1A] border border-[#262626] overflow-hidden group"
  >
    <img
      src={JELLO}
      alt="Colorful $1 jell-o shots"
      className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
    <div className="absolute top-5 left-5 tilt-l border border-[#FFB800]/60 bg-black/70 backdrop-blur px-3 py-1.5 font-mono-plex text-[10px] tracking-[0.25em] uppercase text-[#FFB800]">
      Legendary
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
      <div className="font-display text-6xl md:text-8xl leading-none text-[#FFB800] neon-amber">
        $1
      </div>
      <div className="mt-2 font-display text-3xl md:text-5xl text-[#F5F2E6]">
        Jell-O Shots
      </div>
      <p className="mt-3 text-sm md:text-base text-[#A3A3A3] max-w-md">
        The reason your friends will text you tomorrow asking "what happened
        last night?"
      </p>
    </div>
  </div>
);

const GamesCard = () => (
  <div
    data-testid="highlight-games"
    className="grain-card md:col-span-3 md:row-span-1 relative bg-[#1A1A1A] border border-[#262626] overflow-hidden group"
  >
    <img
      src={POOL}
      alt="Pool game in dive bar"
      className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 transition-opacity"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
    <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
      <Gamepad2 size={28} strokeWidth={2.5} className="text-[#FF2A2A]" />
      <div>
        <div className="font-display text-3xl md:text-5xl text-[#F5F2E6]">
          Pool · Darts · Foosball
        </div>
        <div className="mt-1 font-mono-plex text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3]">
          Back room. Always open.
        </div>
      </div>
    </div>
  </div>
);

const HighlightCard = ({ id, icon, title, sub, accent }) => (
  <div
    data-testid={`highlight-${id}`}
    className="grain-card relative md:col-span-2 bg-[#121212] border border-[#262626] hover:border-[#FFB800]/40 transition-colors p-5 md:p-6 flex flex-col justify-between"
  >
    <div>{icon}</div>
    <div>
      <div className="font-display text-2xl md:text-3xl text-[#F5F2E6] leading-tight">
        {title}
      </div>
      <p className="mt-2 text-sm text-[#A3A3A3]">{sub}</p>
    </div>
    <div
      className="absolute left-0 bottom-0 h-[3px] w-12"
      style={{ background: accent }}
    />
  </div>
);

const CARDS = [
  {
    id: "karaoke",
    icon: <Mic2 size={26} strokeWidth={2.5} className="text-[#FF2A2A]" />,
    title: "Karaoke Nights",
    sub: "Sing badly. Sing loud. We promise nobody's filming.",
    accent: "#FF2A2A",
  },
  {
    id: "sports",
    icon: <Tv size={26} strokeWidth={2.5} className="text-[#FFB800]" />,
    title: "Astros & Game Days",
    sub: "TVs on. Crowd loud. Best stop before or after the game.",
    accent: "#FFB800",
  },
  {
    id: "fast",
    icon: <Zap size={26} strokeWidth={2.5} className="text-[#FFB800]" />,
    title: "Fast Pours",
    sub: "Matt, Walter & Sunny will get to you. You won't wait.",
    accent: "#FFB800",
  },
  {
    id: "beer",
    icon: <Beer size={26} strokeWidth={2.5} className="text-[#FF2A2A]" />,
    title: "Cold Beer Lineup",
    sub: "Drafts, bottles, cans. Local + classic. No nonsense.",
    accent: "#FF2A2A",
  },
  {
    id: "byof",
    icon: <Sandwich size={26} strokeWidth={2.5} className="text-[#FFB800]" />,
    title: "Outside Food Welcome",
    sub: "No kitchen, no problem. BYO tacos. Delivery friendly.",
    accent: "#FFB800",
  },
  {
    id: "cocktails",
    icon: <CupSoda size={26} strokeWidth={2.5} className="text-[#FF2A2A]" />,
    title: "Cocktails Without the Markup",
    sub: "Not craft. Not fussy. Strong, cheap, exactly right.",
    accent: "#FF2A2A",
  },
  {
    id: "crowd",
    icon: <Users size={26} strokeWidth={2.5} className="text-[#F5F2E6]" />,
    title: "Strangers Become Regulars",
    sub: "Office workers, tourists, regulars, game-day chaos.",
    accent: "#F5F2E6",
  },
];

const Highlights = () => {
  return (
    <section
      data-testid="highlights-bento"
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#0A0A0A]"
    >
      <SectionHeader />
      <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
        <JelloHeroCard />
        <GamesCard />
        {CARDS.map((c) => (
          <HighlightCard key={c.id} {...c} />
        ))}
      </div>
    </section>
  );
};

export default Highlights;
